import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Db } from "../firebase"; // Υποθέτω ότι το Db εισάγεται από το ίδιο path
import { collection, query, where, getDocs, orderBy, limit } from "firebase/firestore";
import { Container, Row, Col, Card } from "react-bootstrap";
import { ArrowRightShort } from "react-bootstrap-icons"; // Ένα εικονίδιο για το "Διαβάστε Περισσότερα"

/**
 * Component για την εμφάνιση αρχείου άρθρων.
 * Μπορεί να φιλτράρει άρθρα βάσει category ή/και genre από τα URL parameters.
 * * Αναμενόμενες διαδρομές (Routes):
 * /archive                       -> Εμφανίζει όλα τα άρθρα (ή τα πιο πρόσφατα 10)
 * /archive/:category             -> Εμφανίζει άρθρα μόνο για τη συγκεκριμένη category
 * /archive/:category/:genre      -> Εμφανίζει άρθρα για τη συγκεκριμένη category ΚΑΙ το συγκεκριμένο genre
 */
const ArchivePage = () => {
  // Παράμετροι από το URL, π.χ. από το /archive/Music/Rock
  const { category, genre } = useParams(); 
  
  // State για την αποθήκευση των άρθρων
  const [articles, setArticles] = useState([]);
  
  // State για τη φόρτωση
  const [loading, setLoading] = useState(true); 
  
  // State για πιθανά σφάλματα
  const [error, setError] = useState(null); 
  
  // State για να ξέρουμε αν υπάρχουν περισσότερα άρθρα (για μελλοντικό "Load More")
  const [hasMore, setHasMore] = useState(false); 
  
  // Μέγιστος αριθμός άρθρων που ανακτώνται κάθε φορά
  const LIMIT_COUNT = 10; 

  useEffect(() => {
    const fetchArchiveArticles = async () => {
      setLoading(true);
      setError(null);
      
      try {
        let q;
        const postsCollection = collection(Db, "posts");

        // === Δημιουργία δυναμικού Query ===
        let queryConstraints = [];

        // 1. Φιλτράρισμα ανά Category (αν υπάρχει στο URL)
        if (category) {
          console.log(`Filtering by Category: ${category}`);
          queryConstraints.push(where("category", "==", category));
        }

        // 2. Φιλτράρισμα ανά Genre (αν υπάρχει στο URL)
        if (genre) {
          console.log(`Filtering by Genre: ${genre}`);
          // Το genre συνήθως συνδυάζεται με το category, αλλά μπορούμε να το ελέγξουμε και μόνο του
          // Εάν το Firestore index σας δεν το επιτρέπει, ίσως χρειαστεί να έχετε πάντα και category
          queryConstraints.push(where("genre", "==", genre));
        }

        // 3. Ταξινόμηση (Προαιρετικό: εμφανίζουμε τα πιο πρόσφατα πρώτα)
        queryConstraints.push(orderBy("createdAt", "desc")); // Υποθέτοντας ότι υπάρχει πεδίο 'createdAt' (Timestamp)

        // 4. Όριο ανάκτησης για pagination
        queryConstraints.push(limit(LIMIT_COUNT + 1)); // Ζητάμε ένα παραπάνω για να ξέρουμε αν υπάρχουν κι άλλα

        // Δημιουργία του τελικού ερωτήματος
        q = query(postsCollection, ...queryConstraints);


        // === Εκτέλεση του Query ===
        const querySnapshot = await getDocs(q);
        
        // Μετατροπή των αποτελεσμάτων σε array αντικειμένων
        const fetchedArticles = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        
        // Έλεγχος για το αν υπάρχουν περισσότερα άρθρα (pagination check)
        if (fetchedArticles.length > LIMIT_COUNT) {
          setHasMore(true);
          // Κρατάμε μόνο τον αριθμό του ορίου (LIMIT_COUNT)
          setArticles(fetchedArticles.slice(0, LIMIT_COUNT)); 
        } else {
          setHasMore(false);
          setArticles(fetchedArticles);
        }

      } catch (err) {
        console.error("🔥 Σφάλμα κατά την ανάκτηση άρθρων αρχείου:", err);
        setError("Δεν ήταν δυνατή η φόρτωση των άρθρων. Παρακαλώ δοκιμάστε ξανά.");
      } finally {
        setLoading(false);
      }
    };

    fetchArchiveArticles();
    
    // Η εξάρτηση από τα category και genre εξασφαλίζει ότι το fetch τρέχει ξανά όταν αλλάζει η διαδρομή
  }, [category, genre]); 

  // Καθορισμός τίτλου με βάση τις παραμέτρους
  const getArchiveTitle = () => {
    if (category && genre) {
      return `${category} / ${genre}`;
    }
    if (category) {
      return `${category}`;
    }
    return "Όλα τα Άρθρα";
  };
  
  // === RENDER ===
  
  if (loading) {
    return (
      <Container className="my-5 text-center">
        <p>Φόρτωση άρθρων...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="my-5 text-center">
        <p className="text-danger">{error}</p>
      </Container>
    );
  }

  return (
    <Container className="my-5">
      
      {/* Τίτλος Αρχείου */}
      <h1 className="fw-bold mb-4 border-bottom pb-2">
        Archive: {getArchiveTitle()}
      </h1>

      {articles.length === 0 ? (
        <p className="text-center mt-5">
          Δεν βρέθηκαν άρθρα για την επιλογή "{getArchiveTitle()}". 😢
        </p>
      ) : (
        <Row>
          {articles.map((article) => (
            <Col md={6} lg={4} className="mb-4" key={article.id}>
              {/* Card για κάθε άρθρο, όπως και στα Related Posts */}
              <Card className="h-100 border-0 shadow-sm">
                <Link to={`/articles/${article.id}`} className="text-decoration-none">
                  {(article.mainImageUrl || article.image) && (
                    <Card.Img
                      variant="top"
                      src={article.mainImageUrl || article.image}
                      alt={article.title}
                      style={{ height: '200px', objectFit: 'cover' }}
                      className="rounded-top"
                    />
                  )}
                </Link>
                <Card.Body>
                    {/* Category / Genre Breadcrumb */}
                    <small className="text-muted d-block mb-1">
                        {article.category && (
                            <Link to={`/archive/${article.category}`} className="text-decoration-none text-uppercase me-1">
                                {article.category}
                            </Link>
                        )}
                        {article.category && article.genre && " / "}
                        {article.genre && (
                            <Link to={`/archive/${article.category}/${article.genre}`} className="text-decoration-none">
                                {article.genre}
                            </Link>
                        )}
                    </small>
                    
                    <Card.Title className="fw-semibold fs-5 mt-2">
                      <Link
                        to={`/articles/${article.id}`}
                        className="text-decoration-none text-dark"
                      >
                        {article.title}
                      </Link>
                    </Card.Title>
                    <Card.Text className="text-muted small">
                        {/* Σύντομη περιγραφή - Ίσως χρειαστεί να προσθέσετε ένα πεδίο 'excerpt' στη βάση */}
                        {/* Εδώ χρησιμοποιώ ένα απλό substring από το title ως placeholder */}
                        {article.author && `By: ${article.author}`}
                    </Card.Text>
                </Card.Body>
                <Card.Footer className="bg-white border-0 pt-0">
                    <Link
                        to={`/articles/${article.id}`}
                        className="text-decoration-none fw-semibold d-flex align-items-center"
                    >
                        Διαβάστε Περισσότερα <ArrowRightShort size={20} />
                    </Link>
                </Card.Footer>
              </Card>
            </Col>
          ))}
        </Row>
      )}
      
      {/* Πιθανό κουμπί "Load More" */}
      {hasMore && (
        <div className="text-center mt-4">
          <button className="btn btn-outline-primary" /* onClick={loadMore} */ disabled>
            Load More (Δεν έχει υλοποιηθεί)
          </button>
        </div>
      )}

    </Container>
  );
};

export default ArchivePage;